/**
 * The kebab menu — Execute mode's home for whatever `TaskRow`/`RecurringCard`'s row stop
 * showing inline (§11: too many clickable things at once was the complaint, not too much
 * visible info, so badges never move — only buttons collapse in here).
 *
 * One shared component rather than two card-specific ones: a Task row's menu (Snooze, Edit,
 * Break down, Drop) and a Routine row's (Edit, History, Delete) are just different item
 * lists over the same open/close/dismiss behaviour.
 */

import { useEffect, useRef, useState } from "react";
import { Kebab } from "./icons";

export type OverflowMenuItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  /** Red, same as `.btn.danger` — for Delete, never for Drop (STYLE.md: Drop is quiet and
   * reversible, Delete is the one that needs the color). */
  danger?: boolean;
};

export function OverflowMenu({ label, items }: { label: string; items: OverflowMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  // Outside click and Escape both dismiss — only wired up while actually open, so this
  // never adds a document-wide listener for every row on every page that happens to render
  // one of these.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Nothing to offer — e.g. a done task's row, where `TaskRow` never passes Edit/Drop/Split
  // in the first place. A trigger that opens onto an empty list would be worse than no
  // button at all.
  if (items.length === 0) return null;

  return (
    <span className="overflow-menu" ref={ref}>
      <button
        type="button"
        className="btn ghost small"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        <Kebab />
      </button>
      {open && (
        <span className="overflow-menu-list" role="menu">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={`overflow-menu-item${item.danger ? " danger" : ""}`}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
